/**
 * Sobre (menu do nome): a versão no ar e o que entrou em cada versão, como linha do tempo — a atual aberta, as
 * anteriores em cartões que abrem e fecham, cada mudança agrupada pelo tipo. Mesmo desenho do "Sobre" do Precificador.
 * Os dados vêm de packages/shared/src/version.ts (toda entrega acrescenta a sua versão).
 */
import { useState } from 'react';
import clsx from 'clsx';
import { APP_VERSION, RELEASES, type ChangeKind, type Release } from '@pmis/shared';
import { Dialog, Logo } from '../components/ui';
import { fmtDate } from '../lib/format';

const KIND: Record<ChangeKind, { dot: string; chip: string; icon: string; group: string }> = {
  novo: { dot: 'bg-accent', chip: 'bg-accent/10 text-accent border-accent/30', icon: '✦', group: 'Novidades' },
  melhoria: { dot: 'bg-ok', chip: 'bg-ok/10 text-ok border-ok/30', icon: '↗', group: 'Melhorias' },
  correcao: { dot: 'bg-warn', chip: 'bg-warn-bg text-warn border-warn/40', icon: '✓', group: 'Correções' },
  seguranca: { dot: 'bg-err', chip: 'bg-err-bg text-err border-err/40', icon: '⛨', group: 'Segurança' },
  documentacao: { dot: 'bg-teal', chip: 'bg-teal/10 text-teal border-teal/30', icon: '¶', group: 'Documentação' },
};
const ORDER: ChangeKind[] = ['novo', 'melhoria', 'correcao', 'seguranca', 'documentacao'];

function ReleaseCard({ r, current, open, onToggle }: { r: Release; current: boolean; open: boolean; onToggle: () => void }) {
  const kinds = ORDER.filter((k) => r.changes.some((c) => c.kind === k));
  return (
    <li className="relative pl-8">
      <span aria-hidden className={clsx('absolute left-[7px] top-3.5 size-3 rounded-full ring-4 ring-panel', current ? 'bg-accent' : 'bg-line')} />
      <div className={clsx('rounded-xl border transition', current ? 'border-accent/40 bg-panel shadow-sm' : 'border-line bg-panel')}>
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left">
          <span className={clsx('rounded-md px-2 py-0.5 font-mono text-sm font-semibold', current ? 'bg-accent text-accent-fg' : 'bg-panel-2 text-fg')}>{r.version}</span>
          <span className="text-sm text-muted">{fmtDate(r.date)}</span>
          {current ? <span className="rounded-full bg-ok/10 px-2 py-0.5 text-[11px] font-semibold text-ok">versão atual</span> : null}
          <span className="basis-full text-sm font-medium text-fg sm:basis-auto">{r.title}</span>
          <span className="ml-auto flex items-center gap-1.5 text-xs text-muted">
            {kinds.map((k) => <span key={k} aria-hidden className={clsx('size-2 rounded-full', KIND[k].dot)} />)}
            {r.changes.length} {r.changes.length === 1 ? 'item' : 'itens'}
            <svg viewBox="0 0 20 20" className={clsx('size-4 transition', open && 'rotate-180')} aria-hidden>
              <path fill="currentColor" d="M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4Z" />
            </svg>
          </span>
        </button>
        {open ? (
          <div className="space-y-4 border-t border-line px-4 py-4">
            {kinds.map((k) => (
              <div key={k} className="space-y-2">
                <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold', KIND[k].chip)}>
                  <span aria-hidden>{KIND[k].icon}</span>{KIND[k].group}
                </span>
                <ul className="space-y-2">
                  {r.changes.filter((c) => c.kind === k).map((c, i) => (
                    <li key={i} className="flex gap-2.5 text-sm leading-relaxed">
                      <span aria-hidden className={clsx('mt-2 size-1.5 shrink-0 rounded-full', KIND[k].dot)} />
                      <span className="min-w-0 text-fg/90">{c.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

export function AboutDialog({ onClose }: { onClose: () => void }) {
  const [open, setOpen] = useState<Set<string>>(new Set([RELEASES[0]?.version ?? APP_VERSION]));
  const toggle = (v: string) => setOpen((s) => { const n = new Set(s); if (n.has(v)) n.delete(v); else n.add(v); return n; });
  const current = RELEASES[0];
  return (
    <Dialog open onClose={onClose} wide title="Sobre">
      <div className="space-y-5">
        <div className="relative overflow-hidden rounded-xl bg-[#080044] px-5 py-4 text-white">
          <div aria-hidden className="brand-gradient absolute inset-x-0 bottom-0 h-1" />
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-20 size-56 rounded-full bg-[#1a65f2]/30 blur-3xl" />
          <div className="relative flex flex-wrap items-center gap-4">
            <Logo onDark className="h-8" />
            <div className="min-w-0">
              <div className="text-lg font-semibold">PMIS ADD</div>
              <div className="text-sm text-white/70">Gestão de projetos da AD Digital: portfólio, diário, cronograma, horas, custos e Canais FAST</div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-xs uppercase tracking-wide text-white/60">Versão</div>
              <div className="text-2xl font-bold tabular-nums text-[#2fc5b8]">{APP_VERSION}</div>
              <div className="text-xs text-white/60">{current ? fmtDate(current.date) : ''}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
          <span className="font-medium text-fg">O que há de novo</span>
          {ORDER.map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className={clsx('size-2 rounded-full', KIND[k].dot)} />{KIND[k].group}</span>)}
          <button type="button" className="ml-auto text-accent hover:underline" onClick={() => setOpen(new Set(open.size === RELEASES.length ? [] : RELEASES.map((r) => r.version)))}>
            {open.size === RELEASES.length ? 'Recolher todas' : 'Abrir todas'}
          </button>
        </div>
        <ol className="relative space-y-3 before:absolute before:bottom-2 before:left-[12px] before:top-2 before:w-px before:bg-line">
          {RELEASES.map((r) => <ReleaseCard key={r.version} r={r} current={r.version === APP_VERSION} open={open.has(r.version)} onToggle={() => toggle(r.version)} />)}
        </ol>
        <p className="text-xs text-muted">Toda entrega acrescenta aqui o que mudou, quando e o tipo da mudança. Uso exclusivo da AD Digital.</p>
      </div>
    </Dialog>
  );
}
